export type ClerkTokenOptions = { skipCache?: boolean };
export type ClerkTokenGetter = (options?: ClerkTokenOptions) => Promise<string | null>;

const TOKEN_RETRY_DELAYS_MS = [0, 150, 400, 800, 1_600] as const;

/**
 * Clerk can briefly expose signed-in state before its session token is ready.
 * Confirm a usable token before host queries mount, forcing a server refresh
 * after the first cached lookup misses.
 */
export async function waitForClerkSessionToken(
  getToken: ClerkTokenGetter,
  sleep: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds)),
): Promise<string | null> {
  for (let attempt = 0; attempt < TOKEN_RETRY_DELAYS_MS.length; attempt++) {
    const delay = TOKEN_RETRY_DELAYS_MS[attempt] ?? 0;
    if (delay > 0) await sleep(delay);
    try {
      const token = await getToken({ skipCache: attempt > 0 });
      if (token) return token;
    } catch {
      // A transient Clerk/network failure receives the same bounded retry as a
      // not-yet-ready token. The bridge signs out only after every try fails.
    }
  }
  return null;
}

/** Keep the last confirmed in-memory token only while its JWT is still fresh. */
export function createResilientClerkTokenGetter(
  getToken: ClerkTokenGetter,
  initialToken: string,
  onUnavailable: () => void | Promise<void>,
  now: () => number = Date.now,
): ClerkTokenGetter {
  let cachedToken = initialToken;
  let cachedUntilEpochMs = now() + 30_000;
  let recoveryStarted = false;
  return async (options) => {
    try {
      const refreshed = await getToken(options);
      if (refreshed) {
        cachedToken = refreshed;
        cachedUntilEpochMs = now() + 30_000;
      }
    } catch {
      // A still-valid confirmed token can bridge a transient Clerk refresh.
    }
    if (now() < cachedUntilEpochMs) return cachedToken;
    if (!recoveryStarted) {
      recoveryStarted = true;
      void onUnavailable();
    }
    return null;
  };
}
